/**
 * Studio Selector Control Component
 *
 * Reusable dropdown control for selecting a studio when user has multiple
 * studios with Owner/Manager access. Auto-hides if user has only one studio.
 */

import React from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  SelectChangeEvent,
} from '@mui/material';

/**
 * Studio with access information
 * (matches output from useStudioThemeAccess hook)
 */
export interface StudioWithAccess {
  id: string;
  name: string;
  role: string;
}

interface StudioSelectorControlProps {
  /**
   * List of studios user has access to
   */
  studios: StudioWithAccess[];
  /**
   * Currently selected studio ID
   */
  selectedStudioId: string | null;
  /**
   * Handler for studio selection change
   */
  onStudioChange: (studioId: string) => void;
  /**
   * Whether to show the selector even with single studio (default: false)
   */
  alwaysShow?: boolean;
  /**
   * Custom label (default: "Select Studio")
   */
  label?: string;
}

/**
 * Studio selector control that shows a dropdown for multiple studios
 * and auto-hides when user has only one studio.
 */
const StudioSelectorControl: React.FC<StudioSelectorControlProps> = ({
  studios,
  selectedStudioId,
  onStudioChange,
  alwaysShow = false,
  label = 'Select Studio',
}) => {
  // Don't render if only one studio (unless alwaysShow is true)
  if (studios.length === 1 && !alwaysShow) {
    return null;
  }

  // Don't render if no studios
  if (studios.length === 0) {
    return null;
  }

  return (
    <FormControl sx={{ minWidth: { xs: '100%', md: 400 }, maxWidth: 400 }}>
      <InputLabel id="studio-selector-label">{label}</InputLabel>
      <Select
        labelId="studio-selector-label"
        value={selectedStudioId || ''}
        label={label}
        onChange={(event: SelectChangeEvent<string>) => {
          onStudioChange(event.target.value);
        }}
        sx={{
          backgroundColor: 'background.paper',
        }}
      >
        {studios.map(studio => (
          <MenuItem key={studio.id} value={studio.id}>
            <Box>
              <Typography variant="body1">{studio.name}</Typography>
              <Typography variant="caption" color="text.secondary">
                {studio.role === 'owner' ? 'Owner' : 'Manager'}
              </Typography>
            </Box>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default StudioSelectorControl;
